import type { DocPage } from '../types'
import { media } from '../media'


export const fixedMapping: DocPage = {
  id: 'fixed-mapping',
  title: 'Fixed mapping',
  description:
    'Assign constant values to attributes with the Add fixed mapping / Set fixed value modal, including type-specific validation for text, char, integer, decimal, boolean, date, and enum.',
  category: 'Mapping',
  sections: [
    { id: 'overview', title: 'What fixed mapping does' },
    { id: 'when', title: 'When fixed values are applied' },
    { id: 'open', title: 'Open the modal' },
    { id: 'types', title: 'Value types' },
    { id: 'scratch', title: 'Scratch: Set fixed value' },
    { id: 'steps', title: 'Step-by-step' },
    { id: 'tips', title: 'Tips and pitfalls' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'overview',
      text: 'What fixed mapping does',
    },
    {
      type: 'paragraph',
      text: 'A fixed mapping writes a constant value into a target attribute (mapped transfer) or a logical/source attribute value slot (scratch Set fixed value). Use it for status flags, project codes, default dates, enumerator defaults, and other constants that should not come from a varying source field.',
    },
    {
      type: 'figure',
      src: media.addFixedMapping,
      alt: 'Add fixed mapping modal',
      caption:
        'Figure: Add fixed mapping / Set fixed value modal with type-specific value entry.',
      size: 'compact',
    },
    {
      type: 'heading',
      level: 2,
      id: 'when',
      text: 'When fixed values are applied',
    },
    {
      type: 'callout',
      variant: 'info',
      title: 'Order of application',
      text: 'Fixed mappings are applied after attribute mappings during transfer. Plan so that Connect-driven attribute copies run first, then constants overwrite or fill the intended fields.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'open',
      text: 'Open the modal',
    },
    {
      type: 'table',
      headers: ['Context', 'Toolbar / action', 'Submit label'],
      rows: [
        ['Mapped transfer', 'F — Add fixed mapping', 'Add fixed mapping'],
        [
          'Scratch transfer',
          'F — Set fixed value for logical attribute',
          'Set fixed value',
        ],
      ],
    },
    {
      type: 'paragraph',
      text: 'The modal header area shows context such as Collection: … | Type: …. Use Cancel to close without saving. Placeholders look like Enter {name} value; enums use Select value.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'types',
      text: 'Value types',
    },
    {
      type: 'table',
      monoFirst: true,
      headers: ['Type', 'UI guidance', 'Valid input'],
      rows: [
        ['text', 'Enter a text value.', 'Any string appropriate for the attribute.'],
        ['char', 'Enter exactly one character.', 'Single character only.'],
        ['integer', 'Enter a whole number.', 'Integer without fractional part.'],
        ['decimal', 'Enter a decimal number.', 'Numeric value with optional fraction.'],
        ['boolean', 'Choose true or false.', 'true or false'],
        ['date', 'Choose a valid date.', 'Date from the date picker / valid date string.'],
        [
          'enum',
          'Select a value from the enumerator list.',
          'One of the enumerator values defined on the attribute.',
        ],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'scratch',
      text: 'Scratch: Set fixed value',
    },
    {
      type: 'paragraph',
      text: 'Scratch transfers cannot use value mapping. They can still use Set fixed value on logical attributes (and related source-side fixed value entry points). Combine logical attributes with fixed values when you need constants on newly created scratch schema.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'steps',
      text: 'Step-by-step',
    },
    {
      type: 'steps',
      items: [
        'Select the target attribute mapping (mapped) or the logical attribute (scratch) you want to set.',
        'Click F on the mapping toolbar.',
        'Confirm Collection and Type in the modal.',
        'Enter or select the constant according to the type rules above.',
        'Click Add fixed mapping or Set fixed value.',
        'Run Transfer — the constant is applied after attribute mappings.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'tips',
      text: 'Tips and pitfalls',
    },
    {
      type: 'list',
      items: [
        'Prefer value mapping when the constant depends on source conditions; prefer fixed mapping for unconditional constants.',
        'For ENUM, define the enumerator first (PostGIS logical enumerator) before setting a fixed enum value.',
        'Char rejects multi-character strings — use text if you need more than one character.',
        'After model reloads, verify fixed mappings still point at existing attributes; remove inactive mappings if needed.',
      ],
    },
  ],
}
